import { TableColumnsType } from 'antd';
import TableComponent from 'components/Table/TableComponent';
import usePagination from 'hooks/usePagination';
import Content from 'layouts/Content';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { formatCurrency, generateUUID } from 'utils/constants/functions';
import { HocChangePagination } from 'utils/HocChangePagination';

interface ITableMarketCoverageProps {
  loading?: boolean;
  data: any;
  exportExcel?: (columns: any) => void;
}

const TableMarketCoverage = (
  { data, loading, exportExcel }: ITableMarketCoverageProps,
  ref: any,
) => {
  const { page, page_size } = usePagination();

  const columns: TableColumnsType = useMemo(
    () => [
      {
        width: 50,
        title: 'STT',
        align: 'center',
        render: (_: any, __: any, index: number) =>
          index + 1 + page_size * (page - 1),
      },
      {
        width: 150,
        title: 'Tỉnh thành',
        dataIndex: 'city_name',
      },
      {
        width: 150,
        title: 'Mã đội',
        dataIndex: 'team_sales_id',
      },
      {
        title: 'Kỳ này',
        children: [
          {
            type: 'number',
            width: 100,
            title: 'Tổng SL KH',
            dataIndex: 'total_customers_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 210,
            title: 'SL KH kế hoạch cần tiếp cận',
            dataIndex: 'plan_customers_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'SL KH tương tác',
            dataIndex: 'interaction_customers_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 250,
            title: 'SL KH giao dịch thành công',
            dataIndex: 'successful_transactions_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 150,
            title: 'DT công',
            dataIndex: 'total_revenue_current_period',
            align: 'right',
            render: (value: number) =>
              value
                ? formatCurrency({
                    value,
                  })
                : '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'Tỷ lệ tương tác',
            dataIndex: 'interaction_rate_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 220,
            title: 'Tỷ lệ giao dịch thành công',
            dataIndex: 'success_rate_current_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
        ],
      },
      {
        title: 'Cùng kỳ',
        children: [
          {
            type: 'number',
            width: 100,
            title: 'Tổng SL KH',
            dataIndex: 'total_customers_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 210,
            title: 'SL KH kế hoạch cần tiếp cận',
            dataIndex: 'plan_customers_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'SL KH tương tác',
            dataIndex: 'interaction_customers_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 250,
            title: 'SL KH giao dịch thành công',
            dataIndex: 'successful_transactions_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 150,
            title: 'DT công',
            dataIndex: 'total_revenue_same_period_last_year',
            align: 'right',
            render: (value: number) =>
              value
                ? formatCurrency({
                    value,
                  })
                : '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'Tỷ lệ tương tác',
            dataIndex: 'interaction_rate_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 220,
            title: 'Tỷ lệ giao dịch thành công',
            dataIndex: 'success_rate_same_period_last_year',
            align: 'right',
            render: (value: number) => value || '-',
          },
        ],
      },
      {
        title: 'Kỳ trước',
        children: [
          {
            type: 'number',
            width: 100,
            title: 'Tổng SL KH',
            dataIndex: 'total_customers_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 210,
            title: 'SL KH kế hoạch cần tiếp cận',
            dataIndex: 'plan_customers_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'SL KH tương tác',
            dataIndex: 'interaction_customers_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 250,
            title: 'SL KH giao dịch thành công',
            dataIndex: 'successful_transactions_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            currency: 'vi-VN',
            width: 150,
            title: 'DT công',
            dataIndex: 'total_revenue_last_period',
            align: 'right',
            render: (value: number) =>
              value
                ? formatCurrency({
                    value,
                  })
                : '-',
          },
          {
            type: 'number',
            width: 150,
            title: 'Tỷ lệ tương tác',
            dataIndex: 'interaction_rate_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
          {
            type: 'number',
            width: 220,
            title: 'Tỷ lệ giao dịch thành công',
            dataIndex: 'success_rate_last_period',
            align: 'right',
            render: (value: number) => value || '-',
          },
        ],
      },
    ],
    [page, page_size],
  );

  useImperativeHandle(ref, () => ({
    handleExportExcel,
  }));

  const handleExportExcel = () => {
    exportExcel && exportExcel(columns);
  };

  return (
    <Content>
      <TableComponent
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data.items}
        rowKey={(record) => record?.id || generateUUID()}
        loading={loading}
        pagination={{
          current: data.pageInfo?.currentPage,
          pageSize: data.pageInfo?.itemsPerPage,
          total: data.pageInfo?.totalItems,
          onChange: HocChangePagination(),
        }}
      />
    </Content>
  );
};

export default forwardRef(TableMarketCoverage);
